/*
 * wn-pdp-conversion.js — WarmyNest PDP conversion blocks (D-070).
 *
 * Drives the quick-add steppers in Block A (Shop the pieces) and Block B
 * (free-ship + cross-sell). Every tap writes an absolute quantity to the cart via
 * /cart/update.js (adds, changes, or removes the line), then re-renders this
 * section through the Section Rendering API so each stepper reflects its own cart
 * line and the free-shipping bar reflects the live cart total. Taps are applied
 * optimistically for instant feedback and reconciled by the server response, so
 * the two-way binding self-heals. The stock ceiling comes from the server (disabled
 * "+"), and is also enforced client-side so the optimistic step never overshoots.
 *
 * Cart writes elsewhere on the page (main Add to cart, cart drawer edits) refresh
 * this section through the theme's cartUpdate pub/sub when it is available.
 *
 * No framework, no external deps. Event handling is delegated on document so it
 * survives the innerHTML swap. Idempotent: guarded against double-init.
 */
(function () {
  if (window.__wnConvInit) return;
  window.__wnConvInit = true;

  var SOURCE = 'wn-pdp-conversion';
  var ROUTES = window.routes || {};
  var UPDATE_URL = (ROUTES.cart_update_url || '/cart/update') + '.js';

  var pending = {};        // variantId -> absolute qty, batched per flush
  var flushTimer = null;
  var inflight = Promise.resolve();  // cart writes run one at a time, never overlapped
  var seq = 0;             // request order
  var applied = 0;         // newest response already painted

  // ---- helpers ----------------------------------------------------------

  function root() {
    return document.querySelector('[data-wn-conv]');
  }

  function sectionId() {
    var r = root();
    return r ? r.getAttribute('data-section-id') : null;
  }

  // Extract the conversion wrapper's inner HTML from a Section Rendering payload.
  function parseConvInner(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var el = doc.querySelector('[data-wn-conv]');
    return el ? el.innerHTML : null;
  }

  function swapSection(html, token) {
    // Responses can land out of order (our own cart write vs a refresh triggered by
    // the drawer). Without this the older payload wins whenever it arrives last and
    // the free-ship bar sticks one step behind the real cart.
    if (token != null && token < applied) return;
    if (token != null) applied = token;
    var inner = parseConvInner(html);
    var r = root();
    if (inner == null || !r) return;
    // Carry the rail's scroll position across the swap. Every stepper tap re-renders
    // the section, and with a 12-card rail a reset would throw the shopper back to
    // the first card each time they add something.
    var old = r.querySelector('.pieces--rail');
    var keep = old ? old.scrollLeft : 0;
    r.innerHTML = inner;
    var fresh = r.querySelector('.pieces--rail');
    if (fresh && keep) fresh.scrollLeft = keep;
    evalNudge(r);
    initRail(r);
  }

  // Best-effort header cart count refresh. Selector is defensive because the live
  // cart-icon-bubble markup is not part of the canonical mirror.
  function updateBubble(html) {
    var target = document.getElementById('cart-icon-bubble');
    if (!target) return;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var src = doc.getElementById('cart-icon-bubble') || doc.querySelector('.shopify-section') || doc.body;
    if (src) target.innerHTML = src.innerHTML;
  }

  function publishCartUpdate(state) {
    try {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: SOURCE, cartData: state });
      }
    } catch (e) { /* no-op */ }
  }

  function refreshSection() {
    var id = sectionId();
    if (!id) return Promise.resolve();
    var token = ++seq;
    return fetch(window.location.pathname + '?section_id=' + encodeURIComponent(id), { headers: { 'Accept': 'text/html' } })
      .then(function (r) { return r.text(); })
      .then(function (html) { swapSection(html, token); })
      .catch(function () { /* leave current DOM */ });
  }

  // Suppress the cart drawer / notification. Called in the same tick the theme
  // opens it (and again on the next frame) so the drawer never steals focus from
  // Block B on an under-$70 page. Best-effort: uses the theme's own close().
  function closeCartUi() {
    var el = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    if (!el || typeof el.close !== 'function') return;
    try { el.close(); } catch (e) { /* no-op */ }
    requestAnimationFrame(function () { try { el.close(); } catch (e) { /* no-op */ } });
  }

  function scrollToFsfoot() {
    var foot = document.querySelector('[data-wn-fsfoot]');
    if (foot) foot.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  // ---- cart writes ------------------------------------------------------

  function scheduleUpdate(variantId, qty) {
    pending[variantId] = qty;
    clearTimeout(flushTimer);
    flushTimer = setTimeout(flush, 260);
  }

  // /cart/update.js writes absolute quantities against the cart as it was when the
  // request was built. Two in-flight writes therefore clobber each other: the second
  // one carries a stale snapshot of every other line and silently drops the first
  // one's add. Chaining them keeps each write built on the previous result. Clicks
  // during a write keep batching into `pending` and go out on the next link.
  function flush() {
    var updates = pending;
    pending = {};
    if (!Object.keys(updates).length) return;
    inflight = inflight.then(function () { return postUpdates(updates); });
  }

  function postUpdates(updates) {
    var id = sectionId();
    var sections = ['cart-icon-bubble'];
    if (id) sections.unshift(id);
    var token = ++seq;

    return fetch(UPDATE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ updates: updates, sections: sections, sections_url: window.location.pathname })
    })
      .then(function (r) { return r.json(); })
      .then(function (state) {
        if (state && state.sections) {
          if (id && state.sections[id]) swapSection(state.sections[id], token);
          if (state.sections['cart-icon-bubble']) updateBubble(state.sections['cart-icon-bubble']);
        }
        publishCartUpdate(state);
      })
      .catch(function () { return refreshSection(); });
  }

  // ---- optimistic stepper state ----------------------------------------

  function applyOptimistic(qc, qty, cap) {
    qc.setAttribute('data-qty', qty);
    var n = qc.querySelector('.qc__n');
    if (n) n.textContent = qty < 1 ? 1 : qty;
    var plus = qc.querySelector('.qc__plus');
    if (plus) {
      var atCap = (cap !== null && qty >= cap);
      plus.disabled = atCap;
      if (atCap) { plus.setAttribute('aria-disabled', 'true'); } else { plus.removeAttribute('aria-disabled'); }
    }
  }

  // ---- Block A bundle-vs-singles nudge ---------------------------------

  function evalNudge(scope) {
    var nudge = scope.querySelector('[data-wn-nudge]');
    if (!nudge) return;
    var bundlePrice = parseInt(nudge.getAttribute('data-bundle-price'), 10) || 0;
    var qcs = scope.querySelectorAll('.pieces [data-wn-qc]');
    var totalQty = 0, paid = 0;
    qcs.forEach(function (qc) {
      var q = parseInt(qc.getAttribute('data-qty'), 10) || 0;
      var pr = parseInt(qc.getAttribute('data-price'), 10) || 0;
      totalQty += q;
      paid += q * pr;
    });
    nudge.hidden = !(totalQty >= 3 && paid >= bundlePrice);
  }

  // ---- Block B rail: arrow state + position readout ----------------------
  //
  // A bare overflow-x container is unreachable with a plain mouse wheel, and macOS
  // draws the scrollbar as an overlay that is invisible at rest, so without arrows
  // everything past the fourth card is effectively not there. The readout gives the
  // row a length and an end. Rebound after every section swap, because the swap
  // replaces the elements these listeners are attached to.

  function railTrack(scope) {
    return scope.querySelector('.pieces--rail');
  }

  function initRail(scope) {
    var track = railTrack(scope);
    if (!track) return;
    var prev = scope.querySelector('[data-wn-rail-prev]');
    var next = scope.querySelector('[data-wn-rail-next]');
    var readout = scope.querySelector('[data-wn-rail-count]');

    function sync() {
      // offsetParent is null for the cards mobile hides past the eighth, so the
      // count matches what is actually reachable on this viewport.
      var cards = [].filter.call(track.querySelectorAll('.piece'), function (c) {
        return c.offsetParent !== null;
      });
      var total = cards.length;
      var max = track.scrollWidth - track.clientWidth;
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= max - 2;
      if (readout) {
        if (total && max > 2) {
          // Step from the real card pitch rather than width + a guessed gap: the gap
          // is 1.2rem on mobile and 1.6rem on desktop. At the end the leftmost card
          // is never the last one, so clamp instead of reporting "7 / 8" on a rail
          // that has visibly run out.
          var step = cards.length > 1
            ? cards[1].getBoundingClientRect().left - cards[0].getBoundingClientRect().left
            : cards[0].getBoundingClientRect().width;
          var i = track.scrollLeft >= max - 2
            ? total
            : Math.min(total, Math.round(track.scrollLeft / step) + 1);
          readout.textContent = i + ' / ' + total;
        } else {
          readout.textContent = '';
        }
      }
    }

    // Bind once per element. A section swap hands us a fresh track (unflagged, so it
    // binds again); a resize hands us the same one and only needs a re-measure.
    if (!track.__wnRailBound) {
      track.__wnRailBound = true;
      track.addEventListener('scroll', sync, { passive: true });
    }
    sync();
  }

  // ---- delegated events -------------------------------------------------

  document.addEventListener('click', function (e) {
    var r = e.target.closest('[data-wn-conv]');
    if (!r) return;

    var qc = e.target.closest('[data-wn-qc]');
    if (qc) {
      var addBtn = e.target.closest('.qc__add');
      var plusBtn = e.target.closest('.qc__plus');
      var minusBtn = e.target.closest('.qc__minus');
      if (!addBtn && !plusBtn && !minusBtn) return;
      if (e.target.closest('[disabled]')) return;

      var variantId = qc.getAttribute('data-variant-id');
      var cur = parseInt(qc.getAttribute('data-qty'), 10) || 0;
      var cap = qc.hasAttribute('data-cap') ? (parseInt(qc.getAttribute('data-cap'), 10) || 0) : null;

      var next = cur;
      if (addBtn) next = 1;
      else if (plusBtn) next = cur + 1;
      else if (minusBtn) next = cur - 1;
      if (next < 0) next = 0;
      if (cap !== null && next > cap) return; // silent stock ceiling

      applyOptimistic(qc, next, cap);
      scheduleUpdate(variantId, next);
      evalNudge(r);
      return;
    }

    var nav = e.target.closest('[data-wn-rail-prev], [data-wn-rail-next]');
    if (nav) {
      var track = railTrack(r);
      if (track) {
        var dir = nav.hasAttribute('data-wn-rail-next') ? 1 : -1;
        track.scrollBy({ left: dir * track.clientWidth * 0.8, behavior: 'smooth' });
      }
      return;
    }

    if (e.target.closest('[data-wn-nudge-switch]')) {
      var buy = document.querySelector('product-info') ||
                document.querySelector('.product__info-container') ||
                document.querySelector('.product-form');
      if (buy) buy.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  });

  // ---- external cart changes -------------------------------------------

  try {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (evt) {
        if (evt && evt.source === SOURCE) return; // our own writes already swapped

        // HOTFIX 2026-07-27 (方向A): D-070 originally suppressed the native cart
        // notification after the main Add to cart and scrolled to the free-ship
        // footer, relying on its Checkout CTA to surface. On live that CTA did not
        // reliably appear and the scroll landed inconsistently, so shoppers got no
        // "added" confirmation and no path to checkout — orders cratered from 07-24.
        // Restore Dawn's native cart notification: just refresh this section's
        // free-ship bar in the background and let the notification open as usual.
        // (closeCartUi + scrollToFsfoot are now unused, retained pending 方向B.)
        refreshSection();
      });
    }
  } catch (e) { /* no-op */ }

  // ---- initial nudge state ---------------------------------------------

  document.addEventListener('DOMContentLoaded', function () {
    var r = root();
    if (r) { evalNudge(r); initRail(r); }
  });
  if (document.readyState !== 'loading') {
    var r0 = root();
    if (r0) { evalNudge(r0); initRail(r0); }
  }
  // Card widths change at the 750px and 1024px breakpoints, and mobile hides the
  // cards past the eighth, so the arrow state and the count both go stale on resize.
  var resizeTimer = null;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      var r = root();
      if (r) initRail(r);
    }, 200);
  });
})();
